export type ArtifactMedia = { kind: "audio" | "video"; mimeType: string };

const MEDIA_MIME_TYPES: Record<string, ArtifactMedia> = {
  "audio/mpeg": { kind: "audio", mimeType: "audio/mpeg" },
  "audio/mp3": { kind: "audio", mimeType: "audio/mpeg" },
  "audio/mp4": { kind: "audio", mimeType: "audio/mp4" },
  "audio/x-m4a": { kind: "audio", mimeType: "audio/mp4" },
  "audio/aac": { kind: "audio", mimeType: "audio/aac" },
  "audio/wav": { kind: "audio", mimeType: "audio/wav" },
  "audio/x-wav": { kind: "audio", mimeType: "audio/wav" },
  "audio/wave": { kind: "audio", mimeType: "audio/wav" },
  "audio/vnd.wave": { kind: "audio", mimeType: "audio/wav" },
  "audio/ogg": { kind: "audio", mimeType: "audio/ogg" },
  "audio/opus": { kind: "audio", mimeType: "audio/ogg" },
  "audio/webm": { kind: "audio", mimeType: "audio/webm" },
  "audio/flac": { kind: "audio", mimeType: "audio/flac" },
  "audio/x-flac": { kind: "audio", mimeType: "audio/flac" },
  "video/mp4": { kind: "video", mimeType: "video/mp4" },
  "video/webm": { kind: "video", mimeType: "video/webm" },
  "video/quicktime": { kind: "video", mimeType: "video/quicktime" },
};

const MEDIA_EXTENSIONS: Record<string, ArtifactMedia> = {
  ".mp3": MEDIA_MIME_TYPES["audio/mpeg"]!,
  ".m4a": MEDIA_MIME_TYPES["audio/mp4"]!,
  ".aac": MEDIA_MIME_TYPES["audio/aac"]!,
  ".wav": MEDIA_MIME_TYPES["audio/wav"]!,
  ".ogg": MEDIA_MIME_TYPES["audio/ogg"]!,
  ".oga": MEDIA_MIME_TYPES["audio/ogg"]!,
  ".opus": MEDIA_MIME_TYPES["audio/ogg"]!,
  ".weba": MEDIA_MIME_TYPES["audio/webm"]!,
  ".flac": MEDIA_MIME_TYPES["audio/flac"]!,
  ".mp4": MEDIA_MIME_TYPES["video/mp4"]!,
  ".m4v": MEDIA_MIME_TYPES["video/mp4"]!,
  ".webm": MEDIA_MIME_TYPES["video/webm"]!,
  ".mov": MEDIA_MIME_TYPES["video/quicktime"]!,
};

// Containers whose MIME says nothing specific; the file name decides for these.
const GENERIC_MIME_TYPES = new Set(["", "application/octet-stream", "binary/octet-stream"]);

/**
 * Which inline player an artifact gets, and the only Content-Type it may be served with.
 * The stored MIME decides; the extension is consulted only when the MIME is missing or generic,
 * so a text/html or image/svg+xml file named `clip.mp4` is never served as media.
 */
export function artifactMediaKind(
  mimeType: string | null | undefined,
  name: string | null | undefined,
): ArtifactMedia | null {
  const type = (mimeType ?? "").split(";", 1)[0]!.trim().toLowerCase();
  const byType = Object.hasOwn(MEDIA_MIME_TYPES, type) ? MEDIA_MIME_TYPES[type] : undefined;
  if (byType) return byType;
  if (!GENERIC_MIME_TYPES.has(type)) return null;
  const fileName = name ?? "";
  const dot = fileName.lastIndexOf(".");
  if (dot < 0) return null;
  const extension = fileName.slice(dot).toLowerCase();
  return Object.hasOwn(MEDIA_EXTENSIONS, extension) ? MEDIA_EXTENSIONS[extension]! : null;
}
